import React, { useEffect, useState } from "react";
import { keyframes } from "@emotion/react";
import { styled } from "@mui/material/styles";
import { Box, Grid } from "@mui/material";
import * as Sentry from "@sentry/react";
import Typography from "../../../components/common/new/Typography/Typography";
import DashboardReport from "../../../services/api/dashboard";

// Animation
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// Main container
const MainContainer = styled(Box)(() => ({
  background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
  borderRadius: "24px",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  overflow: "hidden",
  position: "relative",
  marginBottom: "14px",
  marginTop: "15px",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #667eea, #764ba2, #667eea)",
    backgroundSize: "200% 100%",
    animation: `${shimmer} 3s infinite`,
  },
}));

// Header
const HeaderSection = styled(Box)(() => ({
  padding: "20px 28px",
  background:
    "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
}));

const HeaderTitle = styled(Typography)(() => ({
  fontSize: "26px",
  fontWeight: 700,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  "@media (max-width: 768px)": {
    fontSize: "20px",
  },
}));

// Field box
const FieldBox = styled(Box)(({ bgcolor }) => ({
  display: "flex",
  flexDirection: "column",
  background: bgcolor || "#f9fafb",
  borderRadius: "12px",
  padding: "12px 10px",
  textAlign: "center",
  transition: "all 0.3s ease",
  cursor: "default",
  border: "1px solid rgba(0,0,0,0.08)",

  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 6px 14px rgba(0,0,0,0.1)",
  },
}));

const FieldTitle = styled(Typography)(() => ({
  fontSize: "12px",
  fontWeight: 600,
  color: "#333",
}));

const FieldValue = styled(Typography)(({ textcolor }) => ({
  fontSize: "20px",
  fontWeight: 700,
  marginTop: "2px",
  marginBottom: "6px",
  color: textcolor || "#111",
}));

// Thin border section divider
// const SectionDivider = styled(Divider)(() => ({
//   margin: "5px 24px",
//   backgroundColor: "rgba(102, 126, 234, 0.2)",
//   height: "1px",
// }));

function Glimpse() {
  const [structureData, setStructureData] = useState({});

  const glipmseReport = async () => {
    try {
      const response = await DashboardReport.getGlipmseReport();
      setStructureData(response.data);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    glipmseReport();
  }, []);

  // Main structure data
  const mainRows = [
    {
      title: "Area",
      value: structureData.areaCount,
      color: "#E3F2FD",
      text: "#1565C0",
    },
    {
      title: "Cluster",
      value: structureData.clusterCount,
      color: "#E0F2F1",
      text: "#00695C",
    },
    {
      title: "Zone",
      value: structureData.zoneCount,
      color: "#E8F5E9",
      text: "#2E7D32",
    },
    {
      title: "Region",
      value: structureData.regionCount,
      color: "#FFF3E0",
      text: "#EF6C00",
    },
    {
      title: "Chapter",
      value: structureData.chapterCount,
      color: "#F3E5F5",
      text: "#8E24AA",
    },
    {
      title: "District",
      value: structureData.districtCount,
      color: "#FFFDE7",
      text: "#F9A825",
    },
    {
      title: "Blocks",
      value: structureData.blockCount,
      color: "#E0F7FA",
      text: "#00838F",
    },
  ];

  // Additional data (YD, WD, MD etc.)
  // const additionalRows = [
  //   {
  //     title: "WD",
  //     value: 12,
  //     color: "#E3F2FD",
  //     text: "#1565C0",
  //   },
  //   {
  //     title: "YWD",
  //     value: 8,
  //     color: "#E8F5E9",
  //     text: "#2E7D32",
  //   },
  //   {
  //     title: "MD",
  //     value: 15,
  //     color: "#FFF3E0",
  //     text: "#EF6C00",
  //   },
  //   {
  //     title: "FD(G)",
  //     value: 20,
  //     color: "#F3E5F5",
  //     text: "#8E24AA",
  //   },
  //   {
  //     title: "YMD",
  //     value: 14,
  //     color: "#FFFDE7",
  //     text: "#F9A825",
  //   },
  //   {
  //     title: "FD(B)",
  //     value: 10,
  //     color: "#E0F7FA",
  //     text: "#00838F",
  //   },
  // ];

  return (
    <MainContainer>
      <HeaderSection>
        <HeaderTitle>BSG Glimpse</HeaderTitle>
      </HeaderSection>

      {/* Main structure data */}
      <Box p={3}>
        <Grid container spacing={1} wrap="nowrap">
          {mainRows.map((row) => (
            <Grid item xs key={row.title}>
              <FieldBox bgcolor={row.color}>
                <FieldTitle>{row.title}</FieldTitle>
                <FieldValue textcolor={row.text}>{row.value ?? "-"}</FieldValue>
              </FieldBox>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Thin border divider */}
      {/* <SectionDivider /> */}

      {/* Additional data section */}
      {/* <Box p={3} pt={1}>
        <Grid container spacing={1}>
          {additionalRows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Grid item xs={6} sm={4} md={2} key={`${row.title}-${index}`}>
              <FieldBox bgcolor={row.color}>
                <FieldTitle>{row.title}</FieldTitle>
                <FieldValue textcolor={row.text}>{row.value ?? "-"}</FieldValue>
              </FieldBox>
            </Grid>
          ))}
        </Grid>
      </Box> */}
    </MainContainer>
  );
}

export default Glimpse;
